import React, { useState, useRef, useEffect, useCallback } from 'react';
import SdmtSymbol from './SdmtSymbol';
import { Mic, Keyboard } from 'lucide-react';
import useTimerState from '../../../components/primitives/useTimerState';
import CountdownGate from '../shared/CountdownGate';
import ClinicalTimer from '../shared/ClinicalTimer';

const TEST_DURATION_MS = 90000;
const INTERVAL_BUCKETS_MS = [30000, 60000];

const wordToDigit = (word) => {
  const map = {
    'один': '1', '1': '1', 'два': '2', '2': '2', 'три': '3', '3': '3',
    'четыре': '4', '4': '4', 'пять': '5', '5': '5', 'шесть': '6', '6': '6',
    'семь': '7', '7': '7', 'восемь': '8', '8': '8', 'девять': '9', '9': '9'
  };
  const cleanWord = word.replace(/[.,!?]/g, '');
  return map[cleanWord] || null;
};

const DUPLICATE_ECHO_WINDOW_MS = 600;

export default function Coding({ question, transcript, status, mode, onChoiceClick, startListening, speak }) {
  const { key, sequence } = question;
  const [inputs, setInputs] = useState(Array(sequence.length).fill(''));
  const [activeIndex, setActiveIndex] = useState(0);
  const [phase, setPhase] = useState('instruction');

  const activeRowRef = useRef(null);
  const startTimeRef = useRef(0);
  const dynamicsRef = useRef([0, 0, 0]);
  const isFinishedRef = useRef(false);
  const inputsRef = useRef(inputs);
  const phaseRef = useRef(phase);
  const onChoiceClickRef = useRef(onChoiceClick);
  const startListeningRef = useRef(startListening);

  const committedCountRef = useRef(0);
  const lastCommittedDigitRef = useRef(null);
  const lastCommitTimeRef = useRef(0);

  useEffect(() => { inputsRef.current = inputs; }, [inputs]);
  useEffect(() => { phaseRef.current = phase; }, [phase]);
  useEffect(() => { onChoiceClickRef.current = onChoiceClick; }, [onChoiceClick]);
  useEffect(() => { startListeningRef.current = startListening; }, [startListening]);

  const handleFinish = useCallback(() => {
    if (isFinishedRef.current) return;
    isFinishedRef.current = true;

    const currentInputs = inputsRef.current;
    let score = 0, errors = 0, omissions = 0;

    sequence.forEach((symId, i) => {
      const ans = currentInputs[i];
      const correct = key[symId];
      if (ans === correct) score++;
      else if (ans === '') omissions++;
      else errors++;
    });

    const payload = JSON.stringify({
      score,
      errors,
      omissions,
      intervals: dynamicsRef.current,
      form: mode === 'show' ? 'written' : 'oral'
    });
    onChoiceClickRef.current(payload);
  }, [key, sequence, mode]);

  // Единый таймер пробы (90 с). Время считается от Date.now(), поэтому
  // троттлинг фоновой вкладки не искажает длительность пробы.
  const timer = useTimerState({ duration: TEST_DURATION_MS, onExpire: handleFinish });
  const timerStartRef = useRef(timer.start);
  useEffect(() => { timerStartRef.current = timer.start; }, [timer.start]);

  // Отсчёт запускается, когда ScaleRunner дочитал инструкцию.
  useEffect(() => {
    if (status === 'show_options' && phase === 'instruction') setPhase('countdown');
  }, [status, phase]);

  // Единственная точка старта пробы: "Начинайте" уже дозвучало.
  // Раньше часы стартовали ДО этого, пациент терял ~1.5 с из 90,
  // причём v1 и v2 теряли по-разному — результаты были несравнимы.
  const handleCountdownReady = useCallback(() => {
    startTimeRef.current = Date.now();
    timerStartRef.current(TEST_DURATION_MS);
    if (mode === 'say' && startListeningRef.current) startListeningRef.current();
    setPhase('test');
  }, [mode]);

  const bumpDynamics = () => {
    const elapsed = Date.now() - startTimeRef.current;
    if (elapsed < INTERVAL_BUCKETS_MS[0]) dynamicsRef.current[0]++;
    else if (elapsed < INTERVAL_BUCKETS_MS[1]) dynamicsRef.current[1]++;
    else dynamicsRef.current[2]++;
  };

  const commitDigit = useCallback((digit) => {
    if (isFinishedRef.current || phaseRef.current !== 'test') return;
    if (!/^[1-9]$/.test(digit)) return;

    setInputs((prev) => {
      const idx = prev.findIndex((v) => v === '');
      if (idx === -1) return prev;

      const next = [...prev];
      next[idx] = digit;
      inputsRef.current = next;

      if (digit === key[sequence[idx]]) bumpDynamics();
      setActiveIndex(Math.min(idx + 1, sequence.length - 1));

      // Таблица заполнена раньше 90 с — проба закончена, держать
      // пациента до конца таймера незачем.
      if (idx === sequence.length - 1) setTimeout(handleFinish, 0);
      return next;
    });
  }, [key, sequence, handleFinish]);

  // Устная форма: распознавание речи.
  useEffect(() => {
    if (mode !== 'say' || phase !== 'test') return;
    if (!transcript) return;

    const words = transcript.toLowerCase().split(/\s+/).filter(Boolean);
    const digitWords = words.map(wordToDigit).filter(Boolean);
    console.log(`[SDMT] @${((Date.now() - startTimeRef.current) / 1000).toFixed(1)}с слов=${words.length} цифр=${digitWords.length} засчитано=${committedCountRef.current} последнее="${words[words.length - 1]}"`);

    const newDigits = digitWords.slice(committedCountRef.current);
    if (newDigits.length === 0) return;
    committedCountRef.current += newDigits.length;

    const now = Date.now();
    for (const digit of newDigits) {
      const isSuspiciousEcho = digit === lastCommittedDigitRef.current
        && now - lastCommitTimeRef.current < DUPLICATE_ECHO_WINDOW_MS;
      if (isSuspiciousEcho) continue;
      lastCommittedDigitRef.current = digit;
      lastCommitTimeRef.current = now;
      commitDigit(digit);
    }
  }, [transcript, mode, phase, commitDigit]);

  // Письменная форма: физическая клавиатура вместо 120 кликов мышью.
  useEffect(() => {
    if (mode !== 'show' || phase !== 'test') return undefined;
    const handleKeyDown = (e) => {
      if (!/^[1-9]$/.test(e.key)) return;
      e.preventDefault();
      commitDigit(e.key);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mode, phase, commitDigit]);

  useEffect(() => {
    if (phase === 'test' && activeRowRef.current) {
      activeRowRef.current.scrollIntoView({ behavior: 'auto', block: 'center' });
    }
  }, [activeIndex, phase]);

  const rowSize = 15;
  const rows = [];
  for (let i = 0; i < sequence.length; i += rowSize) {
    rows.push(sequence.slice(i, i + rowSize));
  }

  if (phase === 'countdown') {
    return <CountdownGate speak={speak} onReady={handleCountdownReady} />;
  }

  return (
    <div className="flex w-full max-w-5xl flex-col items-center gap-4">
      {/* Ключ — по центру. Sticky перенесён с обёртки на сам блок ключа */}
      <div className="sticky top-0 z-20 flex flex-wrap justify-center gap-2 rounded-2xl border border-border bg-secondary p-3 shadow-md">
        {Object.keys(key).map((symId) => (
          <div key={symId} className="flex w-12 flex-col items-center gap-1 border-r border-border px-1 last:border-r-0">
            <span className="flex h-8 items-center justify-center text-primary"><SdmtSymbol id={symId} /></span>
            <div className="flex h-8 w-full items-center justify-center border-b-2 border-border font-bold text-muted-foreground">{key[symId]}</div>
          </div>
        ))}
      </div>

      <div className="flex max-h-[45vh] w-full flex-col gap-2 overflow-y-auto rounded-2xl border-2 border-border bg-white p-2 shadow-lg">
        {rows.map((row, rIdx) => {
          const isActiveRow = activeIndex >= rIdx * rowSize && activeIndex < (rIdx + 1) * rowSize;
          return (
            <div
              key={rIdx}
              ref={isActiveRow ? activeRowRef : null}
              className={`grid gap-1 rounded-xl p-1 transition-all duration-300 ${isActiveRow ? 'z-10 scale-105 border border-accent bg-accent/5 shadow-lg' : 'scale-90 opacity-40'}`}
              style={{ gridTemplateColumns: 'repeat(15, minmax(0, 1fr))' }}
            >
              {row.map((symId, i) => {
                const globalIndex = rIdx * rowSize + i;
                return (
                  <div key={globalIndex} className={`flex flex-col items-center gap-1 transition-all ${activeIndex === globalIndex ? 'scale-110' : 'scale-100'}`}>
                    <span className="flex h-6 items-center justify-center text-primary"><SdmtSymbol id={symId} size={24} /></span>
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-md border-2 text-center text-base font-bold ${activeIndex === globalIndex ? 'border-accent bg-accent/10 ring-1 ring-accent' : 'border-border bg-background'} ${inputs[globalIndex] ? 'text-primary' : 'text-transparent'}`}
                    >
                      {inputs[globalIndex] || '0'}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Нижняя панель: карточка режима по центру, таймер — в правом нижнем углу */}
      <div className="sticky bottom-4 z-30 flex w-full items-end gap-4">
        <div className="flex-1" aria-hidden />
        {mode === 'show' && (
          <div className="rounded-2xl border-2 border-border bg-background p-4 shadow-2xl">
            <p className="mb-2 flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <Keyboard className="size-4" /> Печатайте цифры с клавиатуры или нажимайте кнопки
            </p>
            <div className="mx-auto grid max-w-[240px] grid-cols-3 gap-3">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((num) => (
                <button
                  key={num}
                  onClick={() => commitDigit(num)}
                  disabled={isFinishedRef.current || phase !== 'test'}
                  className="flex h-16 w-16 items-center justify-center rounded-2xl border-2 border-border bg-secondary text-2xl font-bold text-primary shadow-sm transition-all hover:bg-accent/10 active:scale-95"
                >
                  {num}
                </button>
              ))}
            </div>
          </div>
        )}
        {mode === 'say' && (
          <div className="flex max-w-md flex-col items-center gap-2 rounded-2xl border-2 border-border bg-background p-4 shadow-2xl">
            <Mic className="size-12 animate-bounce text-red-500" />
            <p className="text-sm font-medium text-muted-foreground">Называйте цифры по очереди</p>
            {transcript && <p className="w-full truncate text-center text-lg font-bold text-primary">{transcript}</p>}
          </div>
        )}
        <div className="flex flex-1 items-end justify-end">
          {phase === 'test' && <ClinicalTimer remainingSec={timer.remainingSec} percent={timer.percent} />}
        </div>
      </div>
    </div>
  );  
}
